/**
 * Font decode: one Font resource body in, a `FontResource` out, from a file or from a
 * scene's own scope. Both paths keep every non-identity property raw (see `types.ts`).
 */
import { unquoteString } from '../../../parser/utils';
import { stringArrayBodies } from '../../../godot/variant/variantParser.js';
import { findExtResource, findSubResource, parseResourceReference } from '../../SubResourceResolver';
import { extResourcePathsById, resolveRefToResourcePath, subResourceTypeGate } from '../../subResourcePath';
/** The three type names this slice claims: the gate both decode paths apply to a `SubResource` ref. */
export const FONT_SUB_RESOURCE_TYPES = new Set([
    'FontFile',
    'SystemFont',
    'FontVariation',
]);
/**
 * Every `ExtResource(...)`/`SubResource(...)` reference literal inside a value, from
 * `fallbacks = Array[Font]([ExtResource("a"), ExtResource("b")])` or a bare single
 * reference, which comes back as a one-element list.
 */
function extractResourceRefs(value) {
    const refs = [];
    const re = /(?:ExtResource|SubResource)\s*\(\s*"[^"]+"\s*\)/g;
    let match;
    while ((match = re.exec(value)) !== null)
        refs.push(match[0]);
    return refs;
}
/**
 * `PackedStringArray("a", "b")`, `Array[String](["a", "b"])` or `["a", "b"]` → `["a", "b"]`, each element decoded as
 * `unquoteString` decodes it, so `\n` is a newline and not the letter `n`. Empty for any other value.
 */
function parsePackedStringArray(value) {
    return stringArrayBodies(value)?.map(unquoteString) ?? [];
}
/**
 * Decode one file-backed Font resource body, awaiting `loadFont` for each Font-valued
 * property. `selfPath` is the owning file that `SubResource` refs resolve against
 * (`resolveRefToResourcePath`'s `res://file.tres::SubId` form), never the address being built.
 */
export async function decodeFont(selfPath, resourceType, properties, extResources, subResources, loadFont) {
    const extPathById = extResourcePathsById(extResources);
    const gate = subResourceTypeGate(subResources, FONT_SUB_RESOURCE_TYPES);
    const resolveRef = async (ref) => {
        const address = resolveRefToResourcePath(ref, extPathById, selfPath, gate);
        return address ? loadFont(address) : null;
    };
    switch (resourceType) {
        case 'SystemFont': {
            const { font_names, ...rest } = properties;
            return {
                kind: 'system',
                fontNames: font_names !== undefined ? parsePackedStringArray(font_names) : [],
                properties: rest,
            };
        }
        case 'FontVariation': {
            const { base_font, ...rest } = properties;
            const baseFont = await resolveRef(base_font);
            return { kind: 'variation', baseFont, properties: rest };
        }
        case 'FontFile': {
            const { fallbacks, ...rest } = properties;
            const fallbackRefs = fallbacks !== undefined ? extractResourceRefs(fallbacks) : [];
            const resolved = await Promise.all(fallbackRefs.map((ref) => resolveRef(ref)));
            return {
                kind: 'file',
                bytes: undefined,
                mimeType: undefined,
                fallbacks: resolved.filter((f) => f !== null),
                properties: rest,
            };
        }
        default:
            throw new Error(`Unsupported font resource type: ${resourceType}`);
    }
}
/**
 * Resolve a Font reference in a scene's own scope, synchronously, since the solve walk
 * cannot await: `ExtResource` through the font cache, and `SubResource` by decoding the
 * sibling as `decodeFont` does. The loader cannot serve it: `parseTresFile` requires a
 * `[gd_resource]` header, which a `.tscn` lacks.
 */
export function resolveInlineFontResource(ref, externalResources, internalResources, fontCache, pending, 
/**
 * SubResource ids already on this walk. `base_font`/`fallbacks` can name a sibling
 * that names them back, and this resolver runs inside the Control rect solve, so an
 * unguarded cycle blows the stack out of a React render.
 */
visiting = new Set()) {
    if (!ref)
        return null;
    const parsed = parseResourceReference(ref);
    if (!parsed)
        return null;
    if (parsed.type === 'ExtResource') {
        const path = findExtResource(externalResources, parsed.id)?.path;
        if (!path)
            return null;
        const cached = fontCache.getCached(path);
        if (cached === undefined) {
            // Unresolved for this pass. The caller requests it, and the pass after the
            // `font` processor's `loaded`/`failed` event sees the value.
            pending.add(path);
            return null;
        }
        return cached;
    }
    if (visiting.has(parsed.id))
        return null;
    const sub = findSubResource(internalResources, parsed.id);
    if (!sub)
        return null;
    const nowVisiting = new Set(visiting).add(parsed.id);
    const resolveNested = (nestedRef) => resolveInlineFontResource(nestedRef, externalResources, internalResources, fontCache, pending, nowVisiting);
    switch (sub.type) {
        case 'SystemFont': {
            const { font_names, ...rest } = sub.data;
            return {
                kind: 'system',
                fontNames: font_names !== undefined ? parsePackedStringArray(font_names) : [],
                properties: rest,
            };
        }
        case 'FontVariation': {
            const { base_font, ...rest } = sub.data;
            return { kind: 'variation', baseFont: resolveNested(base_font), properties: rest };
        }
        case 'FontFile': {
            const { fallbacks, ...rest } = sub.data;
            const refs = fallbacks !== undefined ? extractResourceRefs(fallbacks) : [];
            const resolved = refs.map(resolveNested).filter((f) => f !== null);
            return { kind: 'file', bytes: undefined, mimeType: undefined, fallbacks: resolved, properties: rest };
        }
        default:
            // Not a Font sub-resource type: the gate the file-backed path applies through
            // `subResourceTypeGate(FONT_SUB_RESOURCE_TYPES)`.
            return null;
    }
}
//# sourceMappingURL=decode.js.map